import { useState } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import AnalyticsPanel from './AnalyticsPanel.jsx';

// Mirrors Portal's /admin: a dedicated destination with its own header +
// horizontal tab bar + content area, not a small centered dialog like this
// app's other overlays (HealthDashboard, UserManagement, BrandKitsPanel).
// FontVault has no client-side router (confirmed: no react-router anywhere
// in this app — every other "view" is a state-driven overlay), so a real
// URL-addressable /admin route isn't available here; this approximates
// Portal's shape as a near-full-screen overlay instead.
//
// Gating is layered, same as Portal's own model (route guard + client tab
// filter + the real enforcement server-side via requireRole('admin')):
//   1. HeaderBar only renders the trigger for isAdmin users.
//   2. This component re-checks isAdmin itself and refuses to render
//      anything if it's somehow reached by a non-admin (defense in depth).
//   3. Every /api/admin/analytics/* route is gated authMiddleware +
//      requireRole('admin') at the app.use() level in server.js — THAT is
//      the actual security boundary, not either of the two checks above.
const TABS: { key: string; label: string }[] = [
  { key: 'analytics', label: 'Analytics' },
];

export default function AdminPanel({ onClose }: { onClose: () => void }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [tab, setTab] = useState('analytics');

  if (user?.role !== 'admin') return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: theme.overlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '96%', maxWidth: 1400, height: '92vh',
          background: theme.bgElevated, borderRadius: 0,
          border: `2px solid ${theme.text}`, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Page header */}
        <div style={{
          padding: '24px 32px', borderBottom: `2px solid ${theme.text}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div>
            <div style={{
              fontSize: 10, fontWeight: 700, color: theme.accent,
              letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4,
            }}>admin</div>
            <h1 style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, margin: 0, fontSize: 28, color: theme.text }}>Admin Panel</h1>
            <div style={{ fontSize: 12, color: theme.textFaint, marginTop: 4 }}>
              admin-only tools for FontVault — visible only to admins, enforced server-side.
            </div>
          </div>
          <span
            onClick={onClose}
            style={{ fontSize: 13, color: theme.textFaint, cursor: 'pointer', lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
          >✕</span>
        </div>

        {/* Outer tab bar */}
        <div style={{ display: 'flex', gap: 14, borderBottom: `1px solid ${theme.border}`, padding: '0 32px', overflowX: 'auto' }}>
          {TABS.map((t) => (
            <span
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '12px 0', fontSize: 12, textTransform: 'lowercase', whiteSpace: 'nowrap',
                borderBottom: tab === t.key ? `2px solid ${theme.accent}` : '2px solid transparent',
                color: tab === t.key ? theme.text : theme.textTertiary,
                cursor: 'pointer',
              }}
            >{t.label}</span>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
          {tab === 'analytics' && <AnalyticsPanel theme={theme} />}
        </div>
      </div>
    </div>
  );
}
